import { Rectangle, jsPDF } from 'jspdf';
import { BaseChartDirective } from 'ng2-charts';
import { IFilters } from 'app/models/filter.models';
import { format } from 'date-fns';
import autoTable, { CellHookData, RowInput } from 'jspdf-autotable';

import { ICitationForExport } from 'app/models/geo-spacial.models';
import { PageType, filtersForPageReport, FilterGroupTitles, FilterTitles } from 'app/models/pdf.models';
import moment from 'moment/moment';

export type Base64Image = {
  imageData?: string;
  width: number;
  height: number;
};

export interface IExportableChart {
  name?: string;
  chart: Base64Image | null;
}

interface IPrintableField {
  name: string;
  values: string[];
}

interface IPrintableFilter {
  filterName: string;
  filterFields: IPrintableField[];
}

export function exportChartToBase64Image(chart?: BaseChartDirective): Base64Image | null {
  const width = chart?.chart?.canvas.width ?? 0;
  const height = chart?.chart?.canvas.height ?? 0;
  const imageData = chart?.toBase64Image();
  if (imageData) {
    return { imageData, width, height };
  }
  return null;
}

export function preparePrintableFilters(allFilters: IFilters, pageType: PageType): IPrintableFilter[] {
  const allowedFilters: string[] = filtersForPageReport[pageType];
  const result: IPrintableFilter[] = [];
  for (const filterGroup of Object.entries(allFilters)) {
    const filterId = filterGroup[0] ?? '';
    const filterName = FilterGroupTitles[filterId] ? FilterGroupTitles[filterId] : filterId;
    const filterFields: IPrintableField[] = [];
    if (allowedFilters.includes(filterId) && filterGroup[1]) {
      const fields = Object.entries(filterGroup[1]);
      for (let i = 0; i < fields.length; i++) {
        const [fieldId, fieldValues] = fields[i];
        if (fieldId && fieldValues) {
          let name: string;
          if (filterId === 'citationTypes') {
            name = `${i + 1}.`;
          } else {
            name = FilterTitles[fieldId] ?? fieldId;
          }
          let values;
          if (fieldId === 'fromDate' || fieldId === 'toDate') {
            values = [moment(fieldValues.toString()).format(MOMENT_DATE_FORMAT)];
          } else {
            values = Array.isArray(fieldValues) ? fieldValues : [fieldValues];
          }
          filterFields.push({ name, values });
        }
      }
    }
    if (filterFields.length > 0) {
      result.push({ filterName, filterFields });
    }
  }
  return result;
}

const MARGIN_REGULAR = 12;
const SECTION_TITLE_FONT_SIZE = 11;
const SECTION_TITLE_HEIGHT = SECTION_TITLE_FONT_SIZE + 2 * MARGIN_REGULAR;
const PAGE_PADDING = { horizontal: 20, vertical: 25 };
const CHART_RECT_MARGIN_VERTICAL = 10;
const DEFAULTS = {
  fontName: 'Helvetica',
  fontSize: 14,
  textColor: '#222',
  drawColor: '#222',
};
const MOMENT_DATE_FORMAT = 'MM/DD/YYYY';
const DATE_FORMAT = 'MM/dd/yyyy';
const TIME_FORMAT = 'hh:mm a';

export class PdfBuilder {
  private yPos: number;
  private doc: jsPDF;
  private maxContentWidth: number;
  private maxContentHeight: number;

  constructor() {
    this.yPos = PAGE_PADDING.vertical;
    this.doc = new jsPDF({ unit: 'px' });
    this.setDefaultOptions();
    const pageSize = {
      width: this.doc.internal.pageSize.getWidth(),
      height: this.doc.internal.pageSize.getHeight(),
    };
    this.maxContentWidth = pageSize.width - 2 * PAGE_PADDING.horizontal;
    this.maxContentHeight = pageSize.height - 2 * PAGE_PADDING.vertical;
  }

  private setDefaultOptions() {
    this.doc
      .setFont(DEFAULTS.fontName, 'normal')
      .setFontSize(DEFAULTS.fontSize)
      .setTextColor(DEFAULTS.textColor)
      .setDrawColor(DEFAULTS.drawColor);
  }

  private addFooters() {
    const pageCount = this.doc.getNumberOfPages();
    this.doc.setFontSize(10);
    this.doc.setFont(DEFAULTS.fontName, 'normal');
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      this.doc.text(
        String(i),
        this.doc.internal.pageSize.getWidth() / 2,
        this.doc.internal.pageSize.getHeight() - PAGE_PADDING.vertical / 2,
        {
          baseline: 'middle',
        },
      );
    }
    this.setDefaultOptions();
  }

  saveToFile(fileName: string) {
    this.addFooters();
    this.doc.save(`${fileName ?? 'analytics-charts'}.pdf`);
  }

  addDocumentTitle(title: string) {
    const TITLE_FONT_SIZE = 16;
    this.doc.setFontSize(TITLE_FONT_SIZE);
    this.doc.text(title, this.doc.internal.pageSize.getWidth() / 2, this.yPos, {
      align: 'center',
    });
    this.yPos += MARGIN_REGULAR;
    this.drawHorizontalDivider(this.yPos);
    this.setDefaultOptions();
  }

  private addSectionTitle(text: string) {
    this.yPos += SECTION_TITLE_FONT_SIZE;
    this.doc.setFontSize(SECTION_TITLE_FONT_SIZE).setTextColor(DEFAULTS.textColor).setFont(DEFAULTS.fontName, 'bold');
    this.doc.text(text, PAGE_PADDING.horizontal, this.yPos, {
      align: 'left',
    });
    this.yPos += MARGIN_REGULAR;
    this.setDefaultOptions();
  }

  private drawHorizontalDivider(y: number, marginHorizontal?: number) {
    const xStart = marginHorizontal ?? 0;
    const xEnd = marginHorizontal
      ? this.doc.internal.pageSize.getWidth() - marginHorizontal
      : this.doc.internal.pageSize.getWidth();
    this.doc.line(xStart, y, xEnd, y);
    this.yPos += MARGIN_REGULAR;
  }

  private calculateTargetImageSize(chartImage: Base64Image) {
    const origAspectRatio = chartImage.width / chartImage.height;
    const targetImageWidth = Math.min(this.maxContentWidth, chartImage.width);
    return {
      width: targetImageWidth,
      height: targetImageWidth / origAspectRatio,
    };
  }

  addCharts(charts: IExportableChart[]): PdfBuilder {
    for (let i = 0; i < charts.length; i++) {
      const chartImage = charts[i]?.chart;
      if (chartImage) {
        let isFirstChartOnPage = i === 0;
        const targetChartSize = this.calculateTargetImageSize(chartImage);
        const chartBlockHeight = SECTION_TITLE_HEIGHT + targetChartSize.height + MARGIN_REGULAR;
        if (this.yPos + chartBlockHeight >= this.maxContentHeight) {
          this.addPage();
          isFirstChartOnPage = true;
          this.yPos = PAGE_PADDING.vertical;
        }
        if (!isFirstChartOnPage) {
          this.drawHorizontalDivider(this.yPos, PAGE_PADDING.horizontal);
        }
        const chartTitle = charts[i]?.name;
        if (chartTitle) {
          this.addSectionTitle(chartTitle);
        }
        const rectHeight = targetChartSize.height;

        const targetRect: Rectangle = {
          x: PAGE_PADDING.horizontal,
          y: this.yPos,
          w: this.maxContentWidth,
          h: rectHeight,
        };
        this.addChartImageToRect(targetRect, chartImage, targetChartSize);

        this.yPos += targetRect.h + CHART_RECT_MARGIN_VERTICAL;
      }
    }
    return this;
  }

  addMapImage(mapImage: Base64Image): PdfBuilder {
    if (mapImage) {
      const targetImageSize = this.calculateTargetImageSize(mapImage);
      const rectHeight = targetImageSize.height;
      if (this.yPos + rectHeight >= this.maxContentHeight) {
        this.addPage();
        this.yPos = PAGE_PADDING.vertical;
      }

      const targetRect: Rectangle = {
        x: PAGE_PADDING.horizontal,
        y: this.yPos,
        w: this.maxContentWidth,
        h: rectHeight,
      };
      this.addChartImageToRect(targetRect, mapImage, targetImageSize);

      this.yPos += targetRect.h + CHART_RECT_MARGIN_VERTICAL;
    }
    return this;
  }

  private drawHorizontalCellBorder = (cellData: CellHookData) => {
    if (cellData.section === 'body') {
      this.doc.setDrawColor('#AAA');
      this.doc.setLineWidth(0.25);
      this.doc.line(
        cellData.cell.x,
        cellData.cell.y + cellData.cell.height,
        cellData.cell.x + cellData.cell.width,
        cellData.cell.y + cellData.cell.height,
      );
    }
  };

  addCitationsTable(citations: ICitationForExport[]): PdfBuilder {
    if (citations?.length) {
      const columns = [
        ['Citation Date', 'Violation Time', 'Day of Week', 'Citation No', 'Location/Address', 'Violations'],
      ];
      const tableData: string[][] = [];
      citations.sort((a, b) => b.id - a.id);
      for (const citation of citations) {
        let datetime: Date | null = null;
        if (citation?.dateTime) {
          datetime = new Date(citation.dateTime);
        }
        const rowData: string[] = [];
        rowData.push(datetime ? format(datetime, DATE_FORMAT) : '-');
        rowData.push(datetime ? format(datetime, TIME_FORMAT) : '-');
        rowData.push(citation.dayOfWeek);
        rowData.push(citation.id.toString());
        rowData.push(citation.location.address);
        const violationNames = citation.citationDetails.map((violation) => violation.name);
        rowData.push(violationNames?.length ? violationNames.join(',\n') : '-');

        tableData.push(rowData);
      }
      autoTable(this.doc, {
        startY: this.yPos,
        head: columns,
        body: tableData,
        theme: 'plain',
        headStyles: { fillColor: '#eee', fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        rowPageBreak: 'avoid',
        margin: {
          horizontal: PAGE_PADDING.horizontal,
          vertical: PAGE_PADDING.vertical,
        },
        willDrawCell: this.drawHorizontalCellBorder,
      });
    }
    return this;
  }

  addFiltersInfo(filters: IPrintableFilter[], departmentName: string | null): PdfBuilder {
    if (departmentName) {
      filters.unshift({
        filterName: '',
        filterFields: [
          {
            name: 'Department',
            values: [departmentName],
          },
        ],
      });
    }
    if (filters.length) {
      this.addSectionTitle('Filter-Based Sampling');
      this.doc.setFontSize(8).setFont(DEFAULTS.fontName, 'normal').setTextColor('#999');
      for (const group of filters) {
        this.addFiltersTable(group);
        this.yPos =
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          ((this.doc as any)?.lastAutoTable?.finalY ?? this.yPos) + MARGIN_REGULAR * 1.3;
      }
      this.setDefaultOptions();
    }
    return this;
  }

  private addFiltersTable(group: IPrintableFilter) {
    this.doc.text(group.filterName, PAGE_PADDING.horizontal, this.yPos, {
      align: 'left',
    });
    this.yPos += 2;
    const tableData: RowInput[] = [];
    for (const filter of group.filterFields) {
      tableData.push([filter.name, filter.values.join(', ')]);
    }
    autoTable(this.doc, {
      startY: this.yPos,
      body: tableData,
      theme: 'plain',
      bodyStyles: { fontSize: 8 },
      rowPageBreak: 'avoid',
      margin: {
        horizontal: PAGE_PADDING.horizontal,
        bottom: MARGIN_REGULAR,
      },
      styles: {
        cellWidth: this.maxContentWidth / 2,
        textColor: '#666',
        cellPadding: [3, 0],
      },
      willDrawCell: this.drawHorizontalCellBorder,
    });
  }

  private addPage() {
    this.doc.addPage();
  }

  private addChartImageToRect(rect: Rectangle, chartImage: Base64Image, imageSize: { width: number; height: number }) {
    if (chartImage.imageData) {
      this.doc.addImage(
        chartImage.imageData,
        'JPG',
        rect.x,
        rect.y,
        imageSize.width,
        imageSize.height,
        undefined,
        'FAST',
      );
    }
  }
}
